import { useState } from "react";
import { Link } from "react-router-dom";
import { APP_LINKS, CUSTOMER_PLANS, VENDOR_PLANS } from "../constants/site";
import { Seo } from "../components/site/Seo";
import { Reveal, SectionHead } from "../components/site/Shared";
import { Icon } from "../components/site/Icon";

const PlanCard = ({ plan, cta, testid, delay }) => (
  <Reveal delay={delay} className={`card-site flex flex-col p-8 ${plan.highlight ? "ring-2 ring-teal" : ""}`}>
    {plan.highlight && (
      <span className="mb-4 w-max rounded-full bg-teal-mist px-3 py-1 text-xs font-bold text-teal-deep">
        Most popular
      </span>
    )}
    <p className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-teal">{plan.name}</p>
    <p className="mt-4 flex items-baseline gap-2">
      <span className="font-display text-5xl font-semibold text-ink">{plan.price}</span>
      <span className="text-sm text-ink/55">{plan.period}</span>
    </p>
    <p className="mt-3 text-sm leading-relaxed text-ink/60">{plan.blurb}</p>
    {plan.benefits && (
      <ul className="mt-6 flex-1 space-y-2.5 border-t border-teal-deep/10 pt-6">
        {plan.benefits.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-ink/70">
            <Icon name="Check" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal" />
            {b}
          </li>
        ))}
      </ul>
    )}
    <a href={cta} data-testid={testid} className={`mt-7 ${plan.highlight ? "btn-primary" : "btn-secondary"} w-full`}>
      Choose {plan.name}
    </a>
  </Reveal>
);

export default function Pricing() {
  const [tab, setTab] = useState("customer");

  return (
    <>
      <Seo
        title="Membership & Pricing — Free, Plus, Pro and Vendor Plans"
        description="HomeAligns pricing: Free ₹0, Plus ₹999 for 6 months, Pro ₹1,999 for 6 months. Money management is included on every plan; Savings Goals are exclusive to Pro. Vendor plans: Starter ₹4,999 for 6 months, Pro ₹8,999 per year."
        path="/pricing"
      />
      <section className="container-site py-20 lg:py-24">
        <SectionHead
          level={1}
          align="center"
          eyebrow="Membership & pricing"
          title="Plans for households and professionals."
          sub="Exact plan names, prices and billing periods — no hidden discounts, no invented offers. Current plan benefits are shown in the app before you choose."
        />

        <Reveal className="mb-12 flex justify-center">
          <div className="inline-flex rounded-full border border-teal-deep/15 bg-white p-1.5" role="tablist" aria-label="Plan type">
            {[
              ["customer", "For Households"],
              ["vendor", "For Service Pros"],
            ].map(([key, label]) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                data-testid={`pricing-tab-${key}`}
                onClick={() => setTab(key)}
                className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-200 ${
                  tab === key ? "bg-teal-deep text-white" : "text-ink/60 hover:text-teal-deep"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </Reveal>

        {tab === "customer" ? (
          <div className="grid gap-6 md:grid-cols-3" data-testid="pricing-customer-grid">
            {CUSTOMER_PLANS.map((p, i) => (
              <PlanCard key={p.name} plan={p} cta={APP_LINKS.registerCustomer} testid={`pricing-customer-${p.name.toLowerCase()}-btn`} delay={i + 1} />
            ))}
          </div>
        ) : (
          <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2" data-testid="pricing-vendor-grid">
            {VENDOR_PLANS.map((p, i) => (
              <PlanCard key={p.name} plan={p} cta={APP_LINKS.registerVendor} testid={`pricing-vendor-${p.name.toLowerCase()}-btn`} delay={i + 1} />
            ))}
          </div>
        )}

        <Reveal className="mx-auto mt-12 max-w-3xl space-y-4">
          <div className="rounded-2xl border border-teal-deep/10 bg-white p-6" data-testid="pricing-money-note">
            <h3 className="flex items-center gap-2 text-sm font-bold text-ink">
              <Icon name="Wallet" className="h-4 w-4 text-teal" /> Money management is for everyone
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">
              The Money tab — rent, utility bills, expenses and insights — is available on{" "}
              <strong className="text-ink">Free, Plus and Pro</strong>. Your plan sets individual
              feature access and usage limits: Free includes 1 active rent agreement, 3 utility
              billers and basic expense recording; Plus adds unlimited billers, AI scanning,
              splitting, budgets, charts and the monthly Money Report PDF; Pro adds unlimited
              agreements, property-wise analytics, family-member insights, yearly reports and{" "}
              <strong className="text-ink">Savings Goals — exclusive to Pro (coming soon)</strong>.
            </p>
          </div>
          <div className="rounded-2xl border border-teal-deep/10 bg-white p-6">
            <h3 className="flex items-center gap-2 text-sm font-bold text-ink">
              <Icon name="FileText" className="h-4 w-4 text-teal" /> Monthly Money Report (Plus & Pro)
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">
              A monthly summary of rent, utility bills, category-wise household expenditure and
              spending comparisons — downloadable as a PDF whenever you want it. Pro reports add
              property-wise and family-member-wise breakdowns. The optional report email is{" "}
              <strong className="text-ink">off by default</strong> and only starts if you switch it on in the app.
            </p>
          </div>
          <div className="rounded-2xl border border-teal-deep/10 bg-white p-6">
            <h3 className="flex items-center gap-2 text-sm font-bold text-ink">
              <Icon name="Check" className="h-4 w-4 text-teal" /> Good to know
            </h3>
            <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ink/60">
              <li>· Payments currently run in <strong>Test Mode</strong> in the app — no real money is charged.</li>
              <li>· Vendor subscriptions buy platform access and listed features; they do not guarantee leads, bookings or revenue.</li>
              <li>· HomeAligns does not deduct commission from bookings. Service-booking payments are separate from membership plans.</li>
              <li>· Existing vendor subscribers keep their current subscription terms.</li>
            </ul>
          </div>
          <p className="text-center text-sm text-ink/55">
            Cancellations and refunds follow our{" "}
            <Link to="/refund-policy" data-testid="pricing-refund-link" className="font-bold text-teal-deep underline underline-offset-4 hover:text-teal">
              Refund & Cancellation Policy
            </Link>
            .
          </p>
        </Reveal>
      </section>
    </>
  );
}
